import React, { useEffect, useMemo } from 'react';
import { LoadingOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import {
  DeviceActionProps,
  DeviceActionStepsProps,
} from '@components/Actions/ActionsModal/types';
import api from '@services/api';
import ActionTypes from '@utils/deviceActions';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Form, FormInstance, Tooltip } from 'antd';
import { useActionsLimit } from '@hooks/useActionsLimit';

interface SuspensionPayloadProps {
  action: ActionTypes.SUSPENSION;
  devices: {
    msisdn: string;
  }[];
}

const useSuspension = (
  devices: {
    msisdn: string;
  }[],
  form: FormInstance<any>,
): DeviceActionProps => {
  const { t } = useTranslation('actions-modal');

  const suspensionType = Form.useWatch('suspensionType', form);
  const eligibles = Form.useWatch('eligibles', form);

  const isSuspension = suspensionType === ActionTypes.SUSPENSION;

  const { actionsLimitTitle, actionsLimitContent, limitHasBeenReached } =
    useActionsLimit({
      devices,
      isActive: isSuspension,
      action: ActionTypes.SUSPENSION,
    });

  const eligibleDevices = useMemo(
    () => eligibles?.nbSuspendable,
    [eligibles?.nbSuspendable],
  );

  useEffect(() => {
    if (devices && isSuspension) {
      const devicesMsisdn = devices?.map(device => device?.msisdn);
      api
        .post(
          '/service-proxy/inventory/MOT/suspension-eligibility',
          devicesMsisdn,
        )
        .then(({ data }) => {
          form.setFieldsValue({
            eligibles:
              data !== ''
                ? data
                : {
                    nbSuspendable: 0,
                  },
          });
        });
    }
  }, [devices, form, isSuspension]);

  const resultEligibleDevices = useMemo(() => {
    let type: 'info' | 'error' = 'info';
    let description = t('suspension-withdrawal.eligible', {
      count: eligibleDevices,
    });
    if (eligibleDevices === 0) {
      type = 'error';
      description = t('suspension-withdrawal.no-eligible');
    } else if (eligibleDevices === devices?.length) {
      description = t('suspension-withdrawal.all-eligible');
    }
    return <Alert showIcon type={type} description={description} />;
  }, [devices, eligibleDevices, t]);

  const steps: DeviceActionStepsProps[] = useMemo(
    () => [
      {
        title: (
          <>
            {t('suspension-withdrawal.eligibility')}
            <Tooltip title={t('suspension-withdrawal.eligibility-info')}>
              <Button
                type="link"
                size="small"
                style={{ marginLeft: 4 }}
                icon={<QuestionCircleOutlined />}
              />
            </Tooltip>
          </>
        ),
        name: 'eligibles',
        showWhenUpload: true,
        content:
          eligibleDevices || eligibleDevices === 0 ? (
            resultEligibleDevices
          ) : (
            <Button
              icon={<LoadingOutlined />}
              type="link"
              size="large"
              disabled
            >
              {t('suspension-withdrawal.checking-eligibility')}
            </Button>
          ),
      },
      {
        title: actionsLimitTitle,
        name: 'actionsLimit',
        content: actionsLimitContent,
        showWhenUpload: true,
        isNotRequired: true,
      },
    ],
    [
      actionsLimitContent,
      actionsLimitTitle,
      eligibleDevices,
      resultEligibleDevices,
      t,
    ],
  );

  const payloadToSend = useMemo(
    (): SuspensionPayloadProps => ({
      action: ActionTypes.SUSPENSION,
      devices,
    }),
    [devices],
  );

  return {
    steps,
    payloadToSend,
    infoMessage: !limitHasBeenReached && t('suspension-withdrawal.info-time'),
    confirmDisabled: limitHasBeenReached,
  };
};

export default useSuspension;
